/**
 * Camera zoom helpers — hardware zoom when supported, CSS scale fallback.
 */

export const ZOOM_STEPS = [1, 1.5, 2];

export function getCameraVideoElement(root) {
  if (!root) {
    return document.getElementById("video");
  }
  return root.querySelector("video#video") || root.querySelector("video");
}

export function getVideoTrack(videoEl) {
  const stream = videoEl?.srcObject;
  if (!stream || typeof stream.getVideoTracks !== "function") {
    return null;
  }
  return stream.getVideoTracks()[0] || null;
}

export function getHardwareZoomRange(track) {
  if (!track || typeof track.getCapabilities !== "function") {
    return null;
  }
  try {
    const caps = track.getCapabilities();
    if (!caps || caps.zoom == null) {
      return null;
    }
    return {
      min: caps.zoom.min ?? 1,
      max: caps.zoom.max ?? 1,
      step: caps.zoom.step ?? 0.1,
    };
  } catch {
    return null;
  }
}

export async function applyCameraZoom(videoEl, zoomFactor) {
  if (!videoEl) {
    return { mode: "none", zoom: 1 };
  }

  const track = getVideoTrack(videoEl);
  const range = getHardwareZoomRange(track);
  let hardwareApplied = false;

  if (track && range && range.max > range.min) {
    // Map UI 1→min, 2→max linearly for our 1–2 zoom steps
    const mapped =
      range.min + ((Math.min(zoomFactor, 2) - 1) / 1) * (range.max - range.min);

    try {
      await track.applyConstraints({
        advanced: [{ zoom: Math.min(range.max, Math.max(range.min, mapped)) }],
      });
      hardwareApplied = true;
      videoEl.style.transform = "";
      return { mode: "hardware", zoom: zoomFactor };
    } catch {
      hardwareApplied = false;
    }
  }

  // CSS fallback for preview
  videoEl.style.transformOrigin = "center center";
  videoEl.style.transform = zoomFactor > 1 ? `scale(${zoomFactor})` : "";
  return { mode: hardwareApplied ? "hardware" : "css", zoom: zoomFactor };
}

/**
 * Capture JPEG, cropping center when CSS zoom is active.
 */
export function takeZoomedPhoto(cameraRef, videoEl, zoomFactor) {
  if (!cameraRef?.current?.takePhoto) {
    return null;
  }

  if (!videoEl || !zoomFactor || zoomFactor <= 1.01) {
    return cameraRef.current.takePhoto();
  }

  // Hardware zoom already updates the stream — CSS scale needs a center crop
  const isCssZoom = /scale\(/.test(videoEl.style.transform || "");
  if (!isCssZoom) {
    return cameraRef.current.takePhoto();
  }

  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  if (!vw || !vh) {
    return cameraRef.current.takePhoto();
  }

  const cropW = vw / zoomFactor;
  const cropH = vh / zoomFactor;
  const sx = (vw - cropW) / 2;
  const sy = (vh - cropH) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cropW);
  canvas.height = Math.round(cropH);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return cameraRef.current.takePhoto();
  }

  ctx.drawImage(videoEl, sx, sy, cropW, cropH, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.92);
}
